import { EVENT_NAMES, EVENT_TYPES, RESOURCE_TYPES } from './constants';
import activityTracker from './tracker';
import { EventName } from './types';

// User actions from the PM doc (ROAS projections, investor report V2). Each is called after the
// action's API call succeeds. The tracker adds the user's role in the module (eventDetails.role).

/* ROAS projections */

// Where a projections request was made: "New Request" in Model Requests (client in financial
// projections, admin in model requests), or "Request Access" on a projections screen's empty state
export type ProjectionsRequestSource =
  | 'model_requests_new_request'
  | 'current_cohorts_request_access'
  | 'unified_projections_request_access';

export const trackProjectionsRequested = (
  gameId: string,
  submoduleType: string | null,
  source: ProjectionsRequestSource,
): void =>
  activityTracker.trackAction({
    eventType: EVENT_TYPES.ROAS_PROJECTIONS,
    eventName: EVENT_NAMES.PROJECTIONS_REQUESTED,
    resourceType: RESOURCE_TYPES.GAME,
    resourceId: gameId,
    eventDetails: { submoduleType, source },
  });

// The screen a custom scenario was created on
export type ScenarioCreateSource =
  | 'future_cohorts_create_scenario'
  | 'unified_projections_create_scenario';

export const trackScenarioCreated = (
  gameId: string,
  scenarioId: string | null,
  source: ScenarioCreateSource,
): void =>
  activityTracker.trackAction({
    eventType: EVENT_TYPES.ROAS_PROJECTIONS,
    eventName: EVENT_NAMES.SCENARIO_CREATED,
    resourceType: RESOURCE_TYPES.GAME,
    resourceId: gameId,
    eventDetails: { scenarioId, source },
  });

// The scenario create APIs type their responses differently; the new id is read from either shape
export const getCreatedScenarioId = (response: any): string | null =>
  response?.data?.predictionRequestId ??
  response?.predictionRequestId ??
  response?.data?.id ??
  null;

/* Investor report V2: the resource is the company (client code) */

export type ShareType = 'invite' | 'reinvite' | 'copy_link';

// 'suggested' by the client; 'approved', 'rejected' or 'pending' (decision undone) by an admin
export type SuggestionAction = 'suggested' | 'approved' | 'rejected' | 'pending';

const trackInvestorReport = (
  eventName: EventName,
  clientCode: string,
  eventDetails: Record<string, unknown>,
): void =>
  activityTracker.trackAction({
    eventType: EVENT_TYPES.INVESTOR_REPORT,
    eventName,
    resourceType: RESOURCE_TYPES.COMPANY,
    resourceId: clientCode,
    eventDetails,
  });

// Create or regenerate the company report
export const trackReportGenerated = (
  clientCode: string,
  gameIds: string[],
  isRegenerate: boolean,
): void => trackInvestorReport(EVENT_NAMES.GENERATED, clientCode, { gameIds, isRegenerate });

// Regenerating the company report with a different set of games. Nothing is sent if the set is
// the same.
export const trackGameListChanged = (
  clientCode: string,
  previousGameIds: string[],
  gameIds: string[],
): void => {
  const addedGameIds = gameIds.filter((gameId) => !previousGameIds.includes(gameId));
  const removedGameIds = previousGameIds.filter((gameId) => !gameIds.includes(gameId));
  if (!addedGameIds.length && !removedGameIds.length) return;

  trackInvestorReport(EVENT_NAMES.GAME_LIST_CHANGED, clientCode, {
    previousGameIds,
    gameIds,
    addedGameIds,
    removedGameIds,
  });
};

export const trackReportRefreshed = (clientCode: string): void =>
  trackInvestorReport(EVENT_NAMES.REFRESHED, clientCode, {});

// Edits suggested on a slide, or an admin's decisions on them
export const trackEditsSuggested = (
  clientCode: string,
  slideId: string,
  action: SuggestionAction,
  suggestionCount: number,
): void =>
  trackInvestorReport(EVENT_NAMES.EDITS_SUGGESTED, clientCode, {
    action,
    slideId,
    suggestionCount,
  });

// An admin review save: one event per decision in it (approved, rejected, back to pending)
export const trackSuggestionsReviewed = (
  clientCode: string,
  slideId: string,
  statuses: Array<string | undefined>,
): void => {
  const counts = statuses.reduce<Record<string, number>>(
    (acc, status) => (status ? { ...acc, [status]: (acc[status] ?? 0) + 1 } : acc),
    {},
  );

  Object.entries(counts).forEach(([status, count]) =>
    trackEditsSuggested(clientCode, slideId, status.toLowerCase() as SuggestionAction, count),
  );
};

// Invite (investors and emails), re-invite (one person), or copy the share link
export const trackReportShared = (
  clientCode: string,
  shareType: ShareType,
  recipientCount: number | null = null,
): void => trackInvestorReport(EVENT_NAMES.SHARED, clientCode, { shareType, recipientCount });
